import { Autocomplete, Chip, TextField, FormHelperText, Box } from '@mui/material';
import { omit } from 'lodash';
import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { WsTypoColors, WsTypoVariants, WsTypography } from 'wisestep-base';

import { ChipStatus, ISkillOption } from '../../helper';
interface ISkillsAutoCompleteProps {
  name: string;
  options: ISkillOption[];
  onChange: (values: ISkillOption[]) => void;
  onValueEnter: (input: string) => Promise<void>;
  initialValue?: ISkillOption[];
  updatedValue?: ISkillOption[];
  label?: string;
  placeholder?: string;
  showMandatorySymbol?: boolean;
  resetValue?: boolean;
  enhanceTriggered?: boolean;
}
const SkillsAutoComplete: React.FC<ISkillsAutoCompleteProps> = ({
  name,
  options,
  onChange,
  initialValue = [],
  onValueEnter,
  label,
  placeholder,
  showMandatorySymbol = false,
  resetValue = false,
  enhanceTriggered = false,
  updatedValue,
  ...props
}) => {
  const { control } = useFormContext();
  const [value, setValue] = useState<ISkillOption[]>(initialValue);
  const [inputValue, setInputValue] = useState('');
  useEffect(() => {
    if (resetValue) {
      setValue([]);
      onChange([]);
    }
  }, [onChange, resetValue]);

  useEffect(() => {
    if (updatedValue && updatedValue.length > 0) {
      setValue(updatedValue);
    }
  }, [updatedValue]);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' || event.key === ',') {
      event.preventDefault();
      const input = (event.target as HTMLInputElement).value.trim();
      if (input.length > 0) {
        const newSkill: ISkillOption = { label: input, status: 'normal' };
        const newValue = [...value, newSkill];
        setValue(newValue);
        onChange(newValue);
        setInputValue('');
      }
    } else {
      const input = (event.target as HTMLInputElement).value.trim();
      if (onValueEnter && input.length > 0) {
        onValueEnter(input);
      }
    }
  };
  const handleAutocompleteChange = (_: any, newValue: string | ISkillOption | null) => {
    if (newValue) {
      const skillToAdd =
        typeof newValue === 'string'
          ? { label: newValue, status: 'normal' as ChipStatus }
          : newValue;
      if (!value.some((skill) => skill.label === skillToAdd.label)) {
        const updatedValue = [...value, skillToAdd];
        setValue(updatedValue);
        onChange(updatedValue);
      }
      setInputValue('');
    }
  };
  const handleChipDelete = (optionToDelete: ISkillOption) => {
    const newValue = value.filter((option) => option.label !== optionToDelete.label);
    setValue(newValue);
    onChange(newValue);
  };
  const renderChip = (option: ISkillOption) => {
    const { status } = option;
    return (
      <Chip
        key={option.label}
        label={option.label}
        onDelete={() => handleChipDelete(option)}
        sx={{
          margin: '4px',
          borderRadius: '4px',
          padding: '2px 8px',
          border: status === 'highlighted' ? '2px solid #67E8F9' : 'none',
          color: '#616893',
          fontWeight: 'bold',
          fontSize: 14,
          height: '32px',
        }}
      />
    );
  };
  return (
    <Box>
      {label && (
        <WsTypography variant={WsTypoVariants.SUB_TITLE}>
          {label} {showMandatorySymbol ? ' *' : ''}
        </WsTypography>
      )}{' '}
      <Controller
        name={name}
        control={control}
        render={({ fieldState: { error } }) => (
          <>
            <Autocomplete
              freeSolo
              options={options}
              getOptionLabel={(option) => (typeof option === 'string' ? option : option.label)}
              inputValue={inputValue}
              onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
              onChange={handleAutocompleteChange}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={placeholder}
                  variant="outlined"
                  fullWidth
                  onKeyDown={handleKeyPress}
                  {...omit(props, ['label'])}
                />
              )}
              renderOption={(props, option) => (
                <li {...props} key={typeof option === 'string' ? option : option.label}>
                  {typeof option === 'string' ? option : option.label}
                </li>
              )}
            />{' '}
            <Box
              sx={{
                border: enhanceTriggered ? '2px solid #4caf50' : '1px solid #e0e0e0',
                borderRadius: '4px',
                padding: '12px',
                minHeight: '120px',
                position: 'relative',
                display: 'flex',
                flexWrap: 'wrap',
                gap: '4',
              }}
            >
              {value.map((option) => renderChip(option))}
              {value.length === 0 && (
                <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.SECONDARY}>
                  No Skills Added
                </WsTypography>
              )}
            </Box>
            {enhanceTriggered && (
              <WsTypography variant={WsTypoVariants.SUB_TITLE} color={WsTypoColors.SUCCESS}>
                Skills enhanced with AI
              </WsTypography>
            )}
            {error && <FormHelperText error>{error.message}</FormHelperText>}
          </>
        )}
      />
    </Box>
  );
};
export default SkillsAutoComplete;
